import { normalizeErrorForLogging } from '@/utils/error-normalizer';
import { SpanStatusCode, trace } from '@opentelemetry/api';
import { NextRequest } from 'next/server';

type RequestContext = {
  ipAddress?: string;
  method?: string;
  url?: string;
  userAgent?: string;
};

class AppInsightsLogger {
  private tracer = trace.getTracer('az-ecomm-web-logger');

  logError(
    error: Error | unknown,
    request?: NextRequest,
    statusCode?: number,
    location?: string
  ): void {
    try {
      const requestContext = this.extractRequestContext(request);
      const prefixedError = normalizeErrorForLogging(error);

      const span = this.tracer.startSpan('server-error', {
        attributes: {
          'error.message': prefixedError.message,
          'error.stack': prefixedError.stack ?? '',
          'error.type': prefixedError.name,
          'http.client-ip': requestContext.ipAddress,
          'http.method': requestContext.method,
          'http.status.code': statusCode,
          'http.url': requestContext.url,
          'http.user-agent': requestContext.userAgent,
          'service.location': location,
        },
      });

      span.setStatus({
        code: SpanStatusCode.ERROR,
        message: prefixedError.message,
      });

      span.recordException(prefixedError);

      span.end();

      if (process.env.NODE_ENV === 'development') {
        console.error('[AppInsights Error]', {
          error,
          requestContext,
        });
      }
    } catch (loggingError) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[AppInsights Logging Failed]', loggingError);
      }
    }
  }

  shouldLogHttpError(statusCode: number): boolean {
    return statusCode >= 500;
  }


  private extractRequestContext(request?: NextRequest): RequestContext {
    if (!request) return {};

    const getHeader = (name: string): string | undefined => {
      try {
        return request.headers?.get?.(name) ?? undefined;
      } catch {
        return undefined;
      }
    };

    const context: RequestContext = {
      ipAddress:
        getHeader('x-forwarded-for')?.split(',')[0].trim() ||
        getHeader('x-real-ip') ||
        getHeader('cf-connecting-ip') ||
        undefined,
      method: request.method,
      url: request.url,
      userAgent: getHeader('user-agent') || undefined,
    };

    return context;
  }
}

export const appInsightsLogger = new AppInsightsLogger();
